import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link2, Copy, Check, ExternalLink, Loader2, ToggleLeft, ToggleRight, Download, Trash2, Facebook, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '').replace(/\/$/, '') || window.location.origin;

const ShareDialog = ({ open, onOpenChange, project }) => {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const shareUrl = share?.share_id ? `${BASE_URL}/share/${share.share_id}` : '';

  const fetchShare = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/shares/project/${project.id}`);
      if (res.data.exists) {
        setShare(res.data);
      } else {
        setShare(null);
      }
    } catch { setShare(null); }
    setLoading(false);
  }, [project?.id]);

  useEffect(() => {
    if (open) { fetchShare(); setError(''); setCopied(false); }
  }, [open, fetchShare]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      const res = await axios.post(`${API}/shares`, {
        project_id: project.id,
        allow_download: false,
      });
      setShare({
        share_id: res.data.share_id,
        is_active: res.data.is_active,
        allow_download: res.data.allow_download,
      });
    } catch {
      setError('Could not create share link');
    }
    setCreating(false);
  };

  const handleToggle = async (field, value) => {
    try {
      await axios.patch(`${API}/shares/${share.share_id}`, { [field]: value });
      setShare(prev => ({ ...prev, [field]: value }));
    } catch {
      setError('Could not update settings');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/shares/${share.share_id}`);
      setShare(null);
    } catch {
      setError('Could not delete share link');
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
  const sharePinterest = () => window.open(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(project?.title || 'Lumera-product')}`, '_blank', 'width=600,height=500');
  const shareEmail = () => window.open(`mailto:?subject=${encodeURIComponent(project?.title || 'Lumera product')}&body=${encodeURIComponent(`Check out this product: ${shareUrl}`)}`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-stone-900">Share product</DialogTitle>
          <p className="font-sans text-sm text-stone-500">Create and manage share links for your product.</p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        ) : !share ? (
          /* No share link exists yet */
          <div className="py-4 text-center">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Link2 className="w-7 h-7 text-primary" />
            </div>
            <p className="font-sans text-sm text-stone-600 mb-6">
              Create a public share link so others can view your product.
            </p>
            <Button
              data-testid="create-share-btn"
              onClick={handleCreate}
              disabled={creating}
              className="bg-[#B49A88] hover:bg-[#B49A88] text-white rounded-full px-6 font-sans font-semibold"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
              Create share link
            </Button>
          </div>
        ) : (
          /* Share link exists */
          <div className="py-2 space-y-5">
            {/* Link display + copy */}
            <div>
              <label className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1.5 block">Share link</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2.5 text-xs font-mono text-stone-600 truncate" data-testid="share-url">
                  {shareUrl}
                </div>
                <Button
                  data-testid="copy-share-link"
                  onClick={copyLink}
                  variant="outline"
                  className="flex-shrink-0 rounded-xl h-10 px-3 border-stone-200"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-stone-500" />}
                </Button>
                <Button
                  data-testid="open-share-link"
                  onClick={() => window.open(shareUrl, '_blank')}
                  variant="outline"
                  className="flex-shrink-0 rounded-xl h-10 px-3 border-stone-200"
                >
                  <ExternalLink className="w-4 h-4 text-stone-500" />
                </Button>
              </div>
              {copied && <p className="font-sans text-xs text-green-600 mt-1">Link copied!</p>}
            </div>

            {/* Share to social */}
            <div>
              <label className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1.5 block">Share via</label>
              <div className="flex gap-2">
                <Button
                  data-testid="share-facebook"
                  onClick={shareFacebook}
                  variant="outline"
                  className="rounded-xl h-9 px-4 text-xs font-sans border-stone-200"
                >
                  <Facebook className="w-3.5 h-3.5 mr-1.5 text-blue-600" /> Facebook
                </Button>
                <Button
                  data-testid="share-pinterest"
                  onClick={sharePinterest}
                  variant="outline"
                  className="rounded-xl h-9 px-4 text-xs font-sans border-stone-200"
                >
                  <PinterestIcon className="w-3.5 h-3.5 mr-1.5 text-[#E60023]" /> Pinterest
                </Button>
                <Button
                  data-testid="share-email"
                  onClick={shareEmail}
                  variant="outline"
                  className="rounded-xl h-9 px-4 text-xs font-sans border-stone-200"
                >
                  <Mail className="w-3.5 h-3.5 mr-1.5 text-stone-500" /> Email
                </Button>
              </div>
            </div>

            {/* Settings toggles */}
            <div className="space-y-3 pt-2 border-t border-stone-100">
              <label className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1 block">Settings</label>

              {/* Active toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-sans text-sm text-stone-700 font-medium">Link active</p>
                  <p className="font-sans text-xs text-stone-400">Andre kan se productet</p>
                </div>
                <button
                  data-testid="toggle-active"
                  onClick={() => handleToggle('is_active', !share.is_active)}
                  className="text-stone-500 hover:text-stone-700 transition-colors"
                  aria-label={share.is_active ? 'Deactivate link' : 'Activate link'}
                >
                  {share.is_active
                    ? <ToggleRight className="w-8 h-8 text-green-500" />
                    : <ToggleLeft className="w-8 h-8 text-stone-300" />}
                </button>
              </div>

              {/* Download toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-sans text-sm text-stone-700 font-medium">Tillat nedlasting</p>
                  <p className="font-sans text-xs text-stone-400">Visitors can download PDF/PNG</p>
                </div>
                <button
                  data-testid="toggle-download"
                  onClick={() => handleToggle('allow_download', !share.allow_download)}
                  className="text-stone-500 hover:text-stone-700 transition-colors"
                  aria-label={share.allow_download ? 'Deaktiver nedlasting' : 'Activeer nedlasting'}
                >
                  {share.allow_download
                    ? <ToggleRight className="w-8 h-8 text-green-500" />
                    : <ToggleLeft className="w-8 h-8 text-stone-300" />}
                </button>
              </div>
            </div>

            {/* Delete */}
            <div className="pt-2 border-t border-stone-100">
              <button
                data-testid="delete-share-btn"
                onClick={handleDelete}
                className="flex items-center gap-1.5 font-sans text-xs text-stone-400 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete share link permanently
              </button>
            </div>
          </div>
        )}

        {error && <p data-testid="share-error" className="font-sans text-xs text-red-500 mt-2">{error}</p>}
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
